import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '@/components/ui/command';
import { Plus, Search, Download, FileText, FileSpreadsheet, Upload, Trash2, ListPlus, FormInput, CalendarIcon, AlertTriangle, Eye, EyeOff, X, Sigma, Flag, FlagOff, LayoutGrid, List, Check, Loader2, Sparkles, ChevronsUpDown } from 'lucide-react';
import { format as fmtDate, parse as parseDate } from 'date-fns';
import CalculatorPage from './CalculatorPage';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const GRADES = ['Sealed', 'Opened', 'Grade A', 'Grade B', 'Grade B-', 'Grade C', 'Grade D', 'DOA', 'N/A'];

// Structured options for the Add-form dropdowns (each also offers "Custom…")
const CUSTOM = '__custom__';
const BATTERY_OPTIONS = [
  { value: 'normal', label: 'Normal (80%+)' },
  { value: 'great', label: 'Great (90%+)' },
  { value: 'Below 79%', label: 'Below 79%' },
];
const ISSUE_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'IC Locked', label: 'IC Locked → iCloud price' },
  { value: 'Carrier Locked', label: 'Carrier Locked → locked price' },
  { value: 'ATT Locked', label: 'ATT Locked → AT&T price' },
  { value: 'Cracked Back', label: 'Cracked Back' },
  { value: 'Cracked Lens', label: 'Cracked Lens' },
  { value: 'Bad ESN', label: 'Bad ESN / Blacklisted' },
  { value: 'No Face ID', label: 'No Face ID' },
];
// Map a stored battery/issue string to the auto-target API key
const batteryKey = (v) => {
  const t = (v || '').toLowerCase();
  if (t.includes('79') || t.includes('below') || t.includes('degrad')) return 'below79';
  if (t.includes('great')) return 'great';
  if (t.includes('normal')) return 'normal';
  return '';
};

// Detect brand from typed model text
function inferBrand(modelText) {
  const t = (modelText || '').toLowerCase();
  if (t.includes('iphone') || t.includes('ipad') || t.includes('apple') || t.includes('macbook') || t.includes('watch')) return 'Apple';
  if (t.includes('samsung') || t.includes('galaxy')) return 'Samsung';
  if (t.includes('pixel')) return 'Google';
  if (t.includes('oneplus')) return 'OnePlus';
  return '';
}

// Local YYYY-MM-DD — avoids the UTC off-by-one bug from toISOString()
const toLocalYMD = (d) => {
  const dt = (d instanceof Date) ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Format YYYY-MM-DD for date storage
const dateValue = (s) => {
  if (!s) return '';
  const d = (typeof s === 'string') ? s.slice(0, 10) : toLocalYMD(s);
  return d;
};
const formatDate = (s) => {
  if (!s) return '';
  const d = dateValue(s);
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y.slice(2)}`;
};
const toJSDate = (s) => {
  if (!s) return null;
  try {
    const v = dateValue(s);
    if (!v) return null;
    return parseDate(v, 'yyyy-MM-dd', new Date());
  } catch {
    return null;
  }
};

/* Single editable cell. type = text | number | date | select | computed */
function EditableCell({ value, type, options, onSave, testId, align, className = '', placeholder = '', readOnly, tone, format, sold, selectable, selected, onCellMouseDown, onCellMouseEnter, shouldSuppressClick, onPlainClick, storageValue, storageOptions, onStorageSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [customMode, setCustomMode] = useState(false);
  const [mOpen, setMOpen] = useState(false);
  const [mSearch, setMSearch] = useState('');
  const [sOpen, setSOpen] = useState(false);
  const [sSearch, setSSearch] = useState('');
  const customModeRef = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const commit = async () => {
    setEditing(false);
    if ((val ?? '') === (value ?? '')) return;
    await onSave(val);
  };

  const displayValue = () => {
    if (value === '' || value == null) return <span className="text-muted-foreground/50">—</span>;
    if (type === 'optcustom') {
      const shown = format === 'grade' ? String(value).replace('Grade ', '') : value;
      return <span className={tone === 'sold' ? 'font-bold text-money' : ''}>{shown}</span>;
    }
    if (format === 'imei') {
      const s = String(value);
      const shown = s.length > 8 ? `…${s.slice(-6)}` : s;
      return <span className={sold ? 'font-bold text-money' : ''} title={s}>{shown}</span>;
    }
    if (format === 'grade') return <span>{String(value).replace('Grade ', '')}</span>;
    if (type === 'number') {
      const n = Number(value);
      return <span className="tabular-nums">${n.toLocaleString()}</span>;
    }
    if (type === 'date') return <span className="tabular-nums">{formatDate(value)}</span>;
    if (type === 'computed') {
      const n = Number(value);
      return (
        <span className={`tabular-nums font-semibold ${n >= 0 ? 'text-money' : 'text-destructive'}`}>
          ${n.toLocaleString()}
        </span>
      );
    }
    return <span className={tone === 'sold' ? 'font-bold text-money' : ''}>{value}</span>;
  };

  if (readOnly || type === 'computed') {
    return (
      <td
        className={`px-1.5 py-1 border-r border-foreground/5 ${align === 'right' ? 'text-right' : ''} ${className} ${selectable ? 'cursor-cell' : ''} ${selected ? 'bg-primary/20 ring-1 ring-inset ring-primary/60' : ''}`}
        data-testid={testId}
        onMouseDown={selectable ? onCellMouseDown : undefined}
        onMouseEnter={selectable ? onCellMouseEnter : undefined}
      >
        {displayValue()}
      </td>
    );
  }

  // Date cells use a calendar popover (matches Sales/Expenses pages)
  if (type === 'date') {
    const selectedDate = toJSDate(value);
    return (
      <td className={`p-0 border-r border-foreground/5 ${align === 'right' ? 'text-right' : ''} ${className}`} data-testid={testId}>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-full h-7 px-1.5 hover:bg-sheet-soft/40 cursor-pointer text-left tabular-nums flex items-center gap-1"
              data-testid={`${testId}-trigger`}
            >
              {value
                ? <span className="text-foreground">{formatDate(value)}</span>
                : <span className="text-muted-foreground/50">—</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
            <Calendar
              mode="single"
              selected={selectedDate || undefined}
              onSelect={async (d) => {
                if (!d) return;
                const iso = fmtDate(d, 'yyyy-MM-dd');
                if (iso !== dateValue(value)) await onSave(iso);
              }}
              initialFocus
            />
            <div className="flex justify-between gap-2 p-2 border-t bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => { await onSave(''); }}
                data-testid={`${testId}-clear`}
              >Clear</Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const today = fmtDate(new Date(), 'yyyy-MM-dd');
                  await onSave(today);
                }}
                data-testid={`${testId}-today`}
              >Today</Button>
            </div>
          </PopoverContent>
        </Popover>
      </td>
    );
  }

  if (type === 'model') {
    const stOpts = storageOptions || [];
    return (
      <td className={`p-0 border-r border-foreground/5 max-w-0 ${className}`} data-testid={testId}>
        <div className="flex items-center h-7 min-w-0">
          <Popover open={mOpen} onOpenChange={setMOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="min-w-0 h-full px-1.5 hover:bg-sheet-soft/40 text-left truncate flex items-center" data-testid={`${testId}-trigger`}>
                {value ? <span className="font-medium truncate">{value}</span> : <span className="text-muted-foreground/50">—</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start" data-testid={`${testId}-popover`}>
              <Command>
                <CommandInput placeholder="Search price sheet…" value={mSearch} onValueChange={setMSearch} />
                <CommandList>
                  <CommandEmpty>
                    {mSearch
                      ? <button type="button" className="text-xs text-primary hover:underline px-2 py-1 block w-full text-left" onMouseDown={async (e) => { e.preventDefault(); setMOpen(false); if (mSearch !== value) await onSave(mSearch); }} data-testid={`${testId}-use-custom`}>Use “{mSearch}” as a custom name</button>
                      : <span className="text-xs text-muted-foreground px-2 py-1 block">No models on your price sheet yet.</span>}
                  </CommandEmpty>
                  <CommandGroup>
                    {(options || []).map(m => (
                      <CommandItem key={m.model} value={m.model} onSelect={async () => { setMOpen(false); if (m.model !== value) await onSave(m.model); }}>
                        <Check className={`mr-2 h-4 w-4 ${value === m.model ? 'opacity-100' : 'opacity-0'}`} />
                        {m.model}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Popover open={sOpen} onOpenChange={(o) => { setSOpen(o); if (!o) setSSearch(''); }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="shrink-0 ml-0.5 mr-1 inline-flex items-center gap-0.5 rounded bg-sheet-soft/50 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground hover:bg-sheet-soft hover:text-foreground transition-colors"
                data-testid={`${testId}-storage-trigger`}
                title="Set storage size for exact pricing"
              >
                {storageValue && storageValue !== 'ANY'
                  ? <span className="font-medium uppercase">{storageValue}</span>
                  : <span className="opacity-70">+ GB</span>}
                <ChevronsUpDown className="h-2.5 w-2.5 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-0" align="start" data-testid={`${testId}-storage-popover`}>
              <Command>
                <CommandInput placeholder="Storage…" value={sSearch} onValueChange={setSSearch} />
                <CommandList>
                  <CommandEmpty>
                    {sSearch
                      ? <button type="button" className="text-xs text-primary hover:underline px-2 py-1 block w-full text-left" onMouseDown={async (e) => { e.preventDefault(); setSOpen(false); if (sSearch !== storageValue && onStorageSave) await onStorageSave(sSearch); }} data-testid={`${testId}-storage-custom`}>Use “{sSearch}”</button>
                      : <span className="text-xs text-muted-foreground px-2 py-1 block">No storages.</span>}
                  </CommandEmpty>
                  <CommandGroup>
                    {stOpts.map(s => (
                      <CommandItem key={s} value={s} onSelect={async () => { setSOpen(false); if (s !== storageValue && onStorageSave) await onStorageSave(s); }}>
                        <Check className={`mr-2 h-4 w-4 ${storageValue === s ? 'opacity-100' : 'opacity-0'}`} />
                        {s}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </td>
    );
  }

  if (!editing) {
    return (
      <td
        className={`px-1.5 py-1 border-r border-foreground/5 cursor-cell hover:bg-sheet-soft/40 ${align === 'right' ? 'text-right' : ''} ${className} ${selected ? 'bg-primary/20 ring-1 ring-inset ring-primary/60' : ''}`}
        onMouseDown={selectable ? onCellMouseDown : undefined}
        onMouseEnter={selectable ? onCellMouseEnter : undefined}
        onClick={() => {
          if (selectable) {
            if (shouldSuppressClick && shouldSuppressClick()) return;
            if (onPlainClick) onPlainClick();
          }
          setVal(value ?? '');
          setEditing(true);
        }}
        data-testid={testId}
      >
        {displayValue()}
      </td>
    );
  }

  if (type === 'select') {
    return (
      <td className={`p-0 border-r border-foreground/5 ${className}`} data-testid={testId}>
        <Select
          value={val || ''}
          onValueChange={async (v) => { setVal(v); setEditing(false); if (v !== value) await onSave(v); }}
          defaultOpen
          onOpenChange={(open) => { if (!open) setEditing(false); }}
        >
          <SelectTrigger className="h-7 border-0 rounded-none bg-background text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
          <SelectContent>
            {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
    );
  }

  if (type === 'optcustom') {
    if (customMode) {
      return (
        <td className={`p-0 border-r border-foreground/5 ${className}`} data-testid={testId}>
          <input
            autoFocus defaultValue={value ?? ''}
            className="w-full h-7 px-1.5 bg-background outline-none focus:ring-2 focus:ring-primary text-xs"
            placeholder={testId.startsWith('cell-issue-') ? 'Issue / deduction, e.g. -$40' : 'Type your own…'}
            onBlur={async (e) => { customModeRef.current = false; setCustomMode(false); setEditing(false); if (e.target.value !== value) await onSave(e.target.value); }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
              if (e.key === 'Escape') { customModeRef.current = false; setCustomMode(false); setEditing(false); }
            }}
            data-testid={`${testId}-custom`}
          />
        </td>
      );
    }
    const known = (options || []).some(o => o.value === value);
    return (
      <td className={`p-0 border-r border-foreground/5 ${className}`} data-testid={testId}>
        <Select
          value={known ? value : ''}
          defaultOpen
          onOpenChange={(open) => { if (!open && !customModeRef.current) setEditing(false); }}
          onValueChange={async (v) => { if (v === CUSTOM) { customModeRef.current = true; setCustomMode(true); return; } setEditing(false); if (v !== value) await onSave(v); }}
        >
          <SelectTrigger className="h-7 border-0 rounded-none bg-background text-xs">
            <SelectValue placeholder={placeholder}>{known ? (options.find(o => o.value === value)?.label) : (value || placeholder)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            <SelectItem value={CUSTOM}>Custom…</SelectItem>
          </SelectContent>
        </Select>
      </td>
    );
  }

  return (
    <td className={`p-0 border-r border-foreground/5 ${className}`} data-testid={testId}>
      <input
        ref={inputRef}
        type={type === 'number' ? 'number' : 'text'}
        step={type === 'number' ? '0.01' : undefined}
        value={val ?? ''}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        data-testid={`${testId}-input`}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); }
        }}
        placeholder={placeholder}
        className={`w-full h-7 px-1.5 bg-background outline-none focus:ring-2 focus:ring-primary text-xs ${align === 'right' ? 'text-right tabular-nums' : ''}`}
      />
    </td>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReplaceAll, setImportReplaceAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [projectionScope, setProjectionScope] = useState('all');
  const today = fmtDate(new Date(), 'yyyy-MM-dd');
  const emptyForm = {
    date_purchased: today, model: '', storage: 'ANY', imei: '', battery_health: 'normal', condition_grade: 'Grade B',
    issue: 'None', purchase_price: '', repair_costs: '0', target_sale_price: '', actual_price: '', sold_date: '',
    purchase_source: 'cash', payment_status: 'paid', payment_destination: 'bank', buyer_name: '', payment_reference: '',
  };
  const [formData, setFormData] = useState(emptyForm);
  const [sheetModels, setSheetModels] = useState([]); // [{model, storages}]
  const [modelPickOpen, setModelPickOpen] = useState(false);
  const [autoInfo, setAutoInfo] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [gradeCustom, setGradeCustom] = useState(false);
  const [batteryCustom, setBatteryCustom] = useState(false);
  const [issueCustom, setIssueCustom] = useState(false);
  const [modelSearch, setModelSearch] = useState('');

  // --- Column visibility (persisted) ---
  const [showIssue, setShowIssue] = useState(() => {
    const v = localStorage.getItem('fliptrack_show_issue');
    return v === null ? true : v === 'true';
  });
  useEffect(() => { localStorage.setItem('fliptrack_show_issue', String(showIssue)); }, [showIssue]);

  // Load price-sheet models (for the form's Model + Storage pickers)
  useEffect(() => {
    axios.get(`${API}/pricesheet/model-options`)
      .then(r => setSheetModels(Array.isArray(r.data?.models) ? r.data.models : []))
      .catch(() => setSheetModels([]));
  }, []);

  // Auto-fill the target sale price from the price sheet as the user picks
  // model / storage / grade / battery / issue in the Add form.
  useEffect(() => {
    if (!addFormOpen) return undefined;
    const isSheetModel = sheetModels.some(m => m.model === formData.model);
    if (!isSheetModel) { setAutoInfo(null); return undefined; }
    let cancelled = false;
    setAutoLoading(true);
    axios.post(`${API}/pricesheet/auto-target`, {
      model: formData.model,
      storage: formData.storage || 'ANY',
      grade: formData.condition_grade,
      battery: batteryKey(formData.battery_health),
      issue: formData.issue,
    }).then(r => {
      if (cancelled) return;
      setAutoInfo(r.data);
      if (r.data?.found && r.data.target != null) {
        setFormData(f => ({ ...f, target_sale_price: String(r.data.target) }));
      } else if (!r.data?.manual) {
        setFormData(f => ({ ...f, target_sale_price: '' }));
      }
    }).catch(() => { if (!cancelled) setAutoInfo(null); })
      .finally(() => { if (!cancelled) setAutoLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFormOpen, formData.model, formData.storage, formData.condition_grade, formData.battery_health, formData.issue, formData.purchase_price, sheetModels]);

  // --- Spreadsheet-style numeric cell selection ---
  const NUM_FIELD_BY_COL = ['battery_health', 'purchase_price', 'target_sale_price', 'target_profit', 'actual_price', 'actual_profit'];
  const [selectedCells, setSelectedCells] = useState(() => new Set());
  const [aggMode, setAggMode] = useState('sum');
  const anchorRef = useRef(null);
  const suppressClickRef = useRef(false);

  // --- Mobile view: cards vs full spreadsheet grid (persisted) ---
  const [mobileView, setMobileView] = useState(() => localStorage.getItem('fliptrack_mobile_view') || 'cards');
  useEffect(() => { localStorage.setItem('fliptrack_mobile_view', mobileView); }, [mobileView]);

  // --- Drag-to-flag "pending to sell" across a contiguous range of rows ---
  const flagDragRef = useRef(null);                      // { startIdx, target, lo, hi }
  const [flagPreview, setFlagPreview] = useState(null);  // { lo, hi, target }
  const commitFlagRef = useRef(null);

  const onFlagDown = (e, rowIndex, item) => {
    e.preventDefault();
    const target = !item.pending_sale;
    flagDragRef.current = { startIdx: rowIndex, target, lo: rowIndex, hi: rowIndex };
    setFlagPreview({ lo: rowIndex, hi: rowIndex, target });
  };
  const onFlagEnter = (rowIndex) => {
    const d = flagDragRef.current;
    if (!d) return;
    const lo = Math.min(d.startIdx, rowIndex);
    const hi = Math.max(d.startIdx, rowIndex);
    flagDragRef.current = { ...d, lo, hi };
    setFlagPreview({ lo, hi, target: d.target });
  };

  useEffect(() => {
    const onUp = () => {
      anchorRef.current = null;
      if (commitFlagRef.current) commitFlagRef.current();
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []);

  // Reassigned every render so it always closes over the latest `filtered`/`setPending`.
  commitFlagRef.current = () => {
    const d = flagDragRef.current;
    if (d) {
      const ids = [];
      for (let i = d.lo; i <= d.hi; i += 1) {
        const r = filtered[i];
        if (r) ids.push(r.id);
      }
      if (ids.length) setPending(ids, d.target);
    }
    flagDragRef.current = null;
    setFlagPreview(null);
  };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load. The fetch runs as a promise chain so state updates happen in
  // async callbacks (not synchronously inside the effect).
  useEffect(() => {
    let active = true;
    axios.get(`${API}/inventory`)
      .then(res => { if (active) setItems(res.data); })
      .catch(() => { if (active) toast.error('Failed to load inventory'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Calculate target/actual profit locally (for instant feedback)
  const computeRow = (item) => {
    const pp = Number(item.purchase_price || 0);
    const rc = Number(item.repair_costs || 0);
    const tsp = item.target_sale_price != null ? Number(item.target_sale_price) : null;
    const ap = item.actual_price != null ? Number(item.actual_price) : null;
    const targetProfit = tsp != null ? +(tsp - pp - rc).toFixed(2) : null;
    const actualProfit = ap != null ? +(ap - pp - rc).toFixed(2) : null;
    return { ...item, target_profit: targetProfit, actual_profit: actualProfit };
  };

  // Build payload from item for backend PUT/POST (full replacement)
  const buildPayload = (item) => ({
    brand: item.brand || inferBrand(item.model || ''),
    model: item.model || '',
    condition: item.condition || '',
    purchase_price: Number(item.purchase_price || 0),
    selling_price: item.selling_price != null && item.selling_price !== '' ? Number(item.selling_price) : null,
    status: (item.actual_price && item.sold_date) ? 'sold' : (item.status || 'in_stock'),
    notes: item.notes || '',
    date_purchased: item.date_purchased || '',
    imei: item.imei || '',
    battery_health: item.battery_health || '',
    condition_grade: item.condition_grade || '',
    issue: item.issue || '',
    storage: item.storage || '',
    repair_costs: Number(item.repair_costs || 0),
    target_sale_price: item.target_sale_price != null && item.target_sale_price !== '' ? Number(item.target_sale_price) : null,
    actual_price: item.actual_price != null && item.actual_price !== '' ? Number(item.actual_price) : null,
    sold_date: item.sold_date || '',
    pending_sale: !!item.pending_sale,
  });

  const updateField = async (item, field, value) => {
    let next = { ...item, [field]: value };
    if ((field === 'actual_price' || field === 'sold_date') && next.actual_price && next.sold_date) {
      next.status = 'sold';
    }
    // When picking a sheet model inline, default its storage to the first one
    if (field === 'model') {
      const mo = sheetModels.find(m => m.model === value);
      next.storage = (mo && mo.storages && mo.storages[0]) || (next.storage || 'ANY');
    }
    if (field === 'purchase_price' && value && !next.date_purchased) next.date_purchased = toLocalYMD(new Date());
    setItems(prev => prev.map(x => x.id === item.id ? next : x));
    try {
      const { data } = await axios.put(`${API}/inventory/${item.id}`, buildPayload(next));
      setItems(prev => prev.map(x => x.id === item.id ? data : x));
      if (data.pricing_note) {
        if (data.target_sale_price == null) toast.warning(data.pricing_note);
        else toast.success(`Target $${Number(data.target_sale_price).toLocaleString()} · ${data.pricing_note}`);
      }
    } catch (e) {
      toast.error('Failed to save');
      fetchItems();
    }
  };

  const addRow = async () => {
    try {
      const t = toLocalYMD(new Date());
      const res = await axios.post(`${API}/inventory`, {
        brand: '', model: '', purchase_price: 0, status: 'in_stock', date_purchased: t,
        condition_grade: 'Grade B', repair_costs: 0, battery_health: 'normal', issue: 'None',
      });
      setItems(prev => [res.data, ...prev]);
      window.scrollTo({ top: 200, behavior: 'smooth' });
    } catch (e) {
      toast.error('Failed to add row');
    }
  };

  const submitForm = async () => {
    if (!formData.model.trim() && !formData.purchase_price) {
      toast.error('Enter at least a phone model or price');
      return;
    }
    try {
      const payload = {
        brand: inferBrand(formData.model),
        model: formData.model || '',
        purchase_price: parseFloat(formData.purchase_price) || 0,
        repair_costs: parseFloat(formData.repair_costs) || 0,
        target_sale_price: formData.target_sale_price ? parseFloat(formData.target_sale_price) : null,
        actual_price: formData.actual_price ? parseFloat(formData.actual_price) : null,
        date_purchased: formData.date_purchased || today,
        sold_date: formData.sold_date || '',
        condition_grade: formData.condition_grade || '',
        imei: formData.imei || '',
        battery_health: formData.battery_health || '',
        issue: formData.issue || '',
        storage: formData.storage || '',
        status: (formData.actual_price && formData.sold_date) ? 'sold' : 'in_stock',
        purchase_source: formData.purchase_source || 'cash',
        payment_status: formData.payment_status || 'paid',
        payment_destination: formData.payment_destination || 'bank',
        buyer_name: formData.buyer_name || '',
        payment_reference: formData.payment_reference || '',
      };
      const res = await axios.post(`${API}/inventory`, payload);
      setItems(prev => [res.data, ...prev]);
      toast.success('Phone added');
      setAddFormOpen(false);
      setFormData(emptyForm);
    } catch (e) {
      toast.error('Failed to add');
    }
  };

  // Instant delete with undo (no confirm dialog for single row)
  const handleDelete = async (item) => {
    if (!item) return;
    const snapshot = item;
    // Optimistic UI removal — feels instant
    setItems(prev => prev.filter(x => x.id !== item.id));
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.delete(item.id);
      return n;
    });

    let undone = false;
    toast(`Deleted ${snapshot.model || 'phone'}`, {
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          // Put it back in place
          setItems(prev => [snapshot, ...prev.filter(x => x.id !== snapshot.id)]);
        },
      },
      duration: 4000,
    });

    // Wait briefly for undo, then commit deletion server-side
    setTimeout(async () => {
      if (undone) return;
      try {
        await axios.delete(`${API}/inventory/${snapshot.id}`);
      } catch (err) {
        toast.error('Failed to delete — restoring');
        setItems(prev => [snapshot, ...prev.filter(x => x.id !== snapshot.id)]);
      }
    }, 4000);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (checked, list) => {
    if (checked) {
      setSelectedIds(new Set(list.map(i => i.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setBulkConfirmOpen(false);
      return;
    }
    setBulkDeleting(true);
    // Optimistic UI removal
    const removed = items.filter(i => selectedIds.has(i.id));
    setItems(prev => prev.filter(i => !selectedIds.has(i.id)));
    try {
      const res = await axios.post(`${API}/inventory/bulk-delete`, { ids });
      toast.success(`Deleted ${res.data.deleted} phone${res.data.deleted === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
    } catch (err) {
      toast.error('Bulk delete failed — restoring');
      setItems(prev => [...removed, ...prev]);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Mark/unmark specific devices as pending to be sold (persisted).
  const setPending = async (ids, pending) => {
    if (!ids || ids.length === 0) return;
    const idSet = new Set(ids);
    setItems(prev => prev.map(x => idSet.has(x.id) ? { ...x, pending_sale: pending } : x));
    try {
      await axios.post(`${API}/inventory/set-pending`, { ids, pending });
      toast.success(pending
        ? `Marked ${ids.length} pending to sell`
        : `Removed ${ids.length} from pending`);
    } catch (e) {
      toast.error('Failed to update pending');
      fetchItems();
    }
  };

  const handleExport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/export/inventory/${fmt}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory.${fmt === 'csv' ? 'csv' : 'pdf'}`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error('Failed to export');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('replace_all', importReplaceAll ? 'true' : 'false');
    try {
      const res = await axios.post(`${API}/import/inventory`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { imported, total_errors, deleted } = res.data;
      const parts = [`Imported ${imported} phones`];
      if (deleted > 0) parts.push(`replaced ${deleted} existing`);
      if (total_errors > 0) parts.push(`${total_errors} errors`);
      toast.success(parts.join(' · '));
      // Clear selections after replace-all
      setSelectedIds(new Set());
      fetchItems();
      setImportDialogOpen(false);
    } catch (err) {
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Stable per-phone sequence #: #1 = phone you bought first, #N = phone you
  // bought most recently. Uses `date_purchased` first (the real purchase date)
  // so imported inventory keeps chronological order — falls back to
  // `created_at` only when the date_purchased field is empty.
  const sequenceById = useMemo(() => {
    const arr = [...items].sort((a, b) => {
      const ak = a.date_purchased || a.created_at || '';
      const bk = b.date_purchased || b.created_at || '';
      if (ak && bk && ak !== bk) return ak < bk ? -1 : 1;
      // tie-break by created_at (import order) then id for stability
      const at = a.created_at || '';
      const bt = b.created_at || '';
      if (at !== bt) return at < bt ? -1 : 1;
      return (a.id || '').localeCompare(b.id || '');
    });
    const map = new Map();
    arr.forEach((it, i) => { map.set(it.id, i + 1); });
    return map;
  }, [items]);

  const filtered = items
    .map(computeRow)
    .filter(item => {
      const matchSearch = `${item.brand} ${item.model} ${item.imei || ''}`.toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        filterStatus === 'all' ? true
        : filterStatus === 'pending' ? !!item.pending_sale && item.status !== 'sold'
        : filterStatus === 'not_pending' ? !item.pending_sale && item.status !== 'sold'
        : item.status === filterStatus;
      return matchSearch && matchStatus;
    })
    // Show newest phone at the top (# N) and oldest at the bottom (# 1)
    .sort((a, b) => (sequenceById.get(b.id) || 0) - (sequenceById.get(a.id) || 0));

  // Clear cell selection on Escape (selection is also cleared in the search /
  // status-filter handlers so it stays in sync without a derived-state effect).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedCells(new Set()); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const startCellDrag = (e, rowIndex, field, col) => {
    if (e.button !== 0) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      suppressClickRef.current = true;
      const key = `${filtered[rowIndex].id}:${field}`;
      setSelectedCells(prev => {
        const n = new Set(prev);
        if (n.has(key)) n.delete(key); else n.add(key);
        return n;
      });
      return;
    }
    anchorRef.current = { rowIndex, col };
    suppressClickRef.current = false;
  };

  const extendCellDrag = (rowIndex, col) => {
    const a = anchorRef.current;
    if (!a) return;
    suppressClickRef.current = true;
    const r0 = Math.min(a.rowIndex, rowIndex), r1 = Math.max(a.rowIndex, rowIndex);
    const c0 = Math.min(a.col, col), c1 = Math.max(a.col, col);
    const next = new Set();
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const row = filtered[r];
        if (row) next.add(`${row.id}:${NUM_FIELD_BY_COL[c]}`);
      }
    }
    setSelectedCells(next);
  };

  const numCellProps = (rowIndex, item, field, col) => ({
    selectable: true,
    selected: selectedCells.has(`${item.id}:${field}`),
    onCellMouseDown: (e) => startCellDrag(e, rowIndex, field, col),
    onCellMouseEnter: () => extendCellDrag(rowIndex, col),
    shouldSuppressClick: () => suppressClickRef.current,
    onPlainClick: () => setSelectedCells(new Set()),
  });

  // Aggregate the selected numeric cells (Google-Sheets-style status bar)
  const selectionAgg = (() => {
    if (selectedCells.size === 0) return null;
    const byId = new Map(filtered.map(r => [r.id, r]));
    const vals = [];
    selectedCells.forEach(key => {
      const idx = key.lastIndexOf(':');
      const id = key.slice(0, idx);
      const field = key.slice(idx + 1);
      const row = byId.get(id);
      if (!row) return;
      const raw = row[field];
      if (raw === '' || raw == null) return;
      const n = Number(raw);
      if (!Number.isNaN(n)) vals.push(n);
    });
    if (vals.length === 0) return { cells: selectedCells.size, count: 0, sum: 0, avg: 0, min: 0, max: 0 };
    const sum = vals.reduce((s, v) => s + v, 0);
    return {
      cells: selectedCells.size,
      count: vals.length,
      sum,
      avg: sum / vals.length,
      min: Math.min(...vals),
      max: Math.max(...vals),
    };
  })();

  const totalValue = items.filter(i => i.status === 'in_stock').reduce((s, i) => s + Number(i.purchase_price || 0), 0);

  // Devices the user has manually flagged as pending to be sold.
  const pendingItems = items.filter(i => i.pending_sale && i.status !== 'sold').map(computeRow);
  const projectionItems = items.filter(i => i.status !== 'sold' && !i.sold_date)
    .filter(i => projectionScope === 'all' || (projectionScope === 'pending' ? i.pending_sale : !i.pending_sale)).map(computeRow);
  const projectionValue = projectionItems.reduce((s, i) => s + Number(i.purchase_price || 0), 0);
  const projectionRevenue = projectionItems.reduce((s, i) => s + Number(i.target_sale_price || 0), 0);
  const projectionProfit = projectionItems.reduce((s, i) => s + Number(i.target_profit || 0), 0);
  const missingTargets = projectionItems.filter(i => i.target_sale_price == null).length;
  const pendingCount = pendingItems.length;
  const pendingExpectedProfit = pendingItems.reduce((s, i) => s + Number(i.target_profit || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary" /></div>;

  const allFilteredSelected = filtered.length > 0 && filtered.every(i => selectedIds.has(i.id));
  const someFilteredSelected = filtered.some(i => selectedIds.has(i.id)) && !allFilteredSelected;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">PHONE INVENTORY</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} devices · In-stock value: <span className="font-semibold text-money">${totalValue.toLocaleString()}</span> · Click any cell to edit
          </p>
          <button
            type="button"
            onClick={() => { setFilterStatus(f => f === 'pending' ? 'all' : 'pending'); setSelectedCells(new Set()); }}
            className={`mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filterStatus === 'pending' ? 'border-sheet bg-sheet-soft/70 text-sheet' : 'border-sheet/40 bg-sheet-soft/40 text-sheet hover:bg-sheet-soft/60'}`}
            data-testid="pending-sale-chip"
            title="Show only devices you've marked pending to be sold"
          >
            <Flag className="h-3.5 w-3.5" />
            {pendingCount} pending to sell
            {pendingCount > 0 && <span className="opacity-70">· inventory ${pendingItems.reduce((sum, item) => sum + Number(item.purchase_price || 0), 0).toLocaleString()} · target profit ${Math.round(pendingExpectedProfit).toLocaleString()}</span>}
            {filterStatus === 'pending' && <span className="ml-1 rounded-full bg-sheet/25 px-1.5 py-0.5 text-[10px] uppercase tracking-wider">Filtered</span>}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setCalculatorOpen(open => !open)} aria-expanded={calculatorOpen} aria-controls="inventory-calculator-panel" data-testid="inventory-calculator-open">
            <Calculator className="h-4 w-4" /> Calculator
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setImportDialogOpen(true)} data-testid="import-inventory-btn">
            <Upload className="h-4 w-4" /> Import
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-inventory-btn">
                <Download className="h-4 w-4" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-inventory-csv">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-inventory-pdf">
                <FileText className="h-4 w-4 mr-2" /> PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={addRow} variant="outline" className="gap-2" data-testid="add-row-btn">
            <ListPlus className="h-4 w-4" /> Quick Row
          </Button>
          <Button onClick={() => setAddFormOpen(true)} data-testid="add-inventory-btn" className="gap-2">
            <FormInput className="h-4 w-4" /> Add Phone
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-sheet/30 bg-sheet-soft/30 px-4 py-3 text-sheet space-y-3" data-testid="inventory-projections">
        <div className="flex flex-wrap items-center gap-2">
          {[['all', 'All unsold'], ['not_pending', 'Not pending'], ['pending', 'Pending to sell']].map(([value, label]) => (
            <button key={value} data-testid={`projection-scope-${value}`} onClick={() => setProjectionScope(value)} aria-pressed={projectionScope === value}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${projectionScope === value ? 'bg-sheet text-white' : 'hover:bg-sheet/10'}`}>{label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm tabular-nums">
          <span data-testid="projection-count"><strong>{projectionItems.length}</strong> devices</span>
          <span data-testid="projection-value">Purchase value <strong>${projectionValue.toLocaleString()}</strong></span>
          <span data-testid="projection-revenue">Projected revenue <strong>${projectionRevenue.toLocaleString()}</strong></span>
          <span data-testid="projection-profit">Target profit <strong>${projectionProfit.toLocaleString()}</strong></span>
        </div>
        {missingTargets > 0 && <p className="text-xs" data-testid="projection-missing-targets">{missingTargets} devices need targets; revenue and profit include priced devices only.</p>}
      </div>
      <Sheet open={calculatorOpen} onOpenChange={setCalculatorOpen} modal={false}>
        <SheetContent side="right" id="inventory-calculator-panel" closeTestId="inventory-calculator-close"
          onInteractOutside={e => e.preventDefault()}
          className="inset-y-3 right-3 h-auto w-[calc(100vw-1.5rem)] max-w-[400px] sm:max-w-[400px] rounded-3xl border p-0 flex flex-col overflow-hidden shadow-2xl"
          data-testid="inventory-calculator-dialog">
          <SheetHeader className="px-4 pt-4 pb-3 pr-10 text-left border-b">
            <SheetTitle>Profit calculator</SheetTitle>
            <SheetDescription className="text-xs">Check a deal with your saved margins.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4" data-testid="inventory-calculator-scroll">
            <CalculatorPage embedded />
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk action bar — visible when items are selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 bg-secondary border border-border px-4 py-2.5 rounded-md"
          data-testid="bulk-action-bar"
        >
          <div className="text-sm">
            <span className="font-semibold text-foreground">{selectedIds.size}</span> selected
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline" size="sm" className="gap-2 border-sheet/50 text-sheet hover:bg-sheet-soft/50"
              onClick={() => setPending(Array.from(selectedIds), true)}
              data-testid="bulk-mark-pending"
            >
              <Flag className="h-3.5 w-3.5" /> Mark pending
            </Button>
            <Button
              variant="ghost" size="sm" className="gap-2 text-muted-foreground"
              onClick={() => setPending(Array.from(selectedIds), false)}
              data-testid="bulk-unmark-pending"
            >
              <FlagOff className="h-3.5 w-3.5" /> Unmark
            </Button>
            <span className="w-px h-5 bg-border mx-0.5" />
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear">
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setBulkConfirmOpen(true)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search phone or IMEI..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedCells(new Set()); }}
            className="pl-10"
            data-testid="inventory-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setSelectedCells(new Set()); }}>
          <SelectTrigger className="w-full sm:w-44" data-testid="inventory-status-filter"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending to sell</SelectItem>
            <SelectItem value="not_pending" data-testid="inventory-filter-not-pending">Not pending (unsold)</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          className="gap-2 sm:w-auto"
          onClick={() => setShowIssue(s => !s)}
          data-testid="toggle-issue-col"
        >
          {showIssue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {showIssue ? 'Hide issue' : 'Show issue'}
        </Button>
        <Button
          variant="outline"
          className="gap-2 md:hidden"
          onClick={() => setMobileView(v => (v === 'cards' ? 'grid' : 'cards'))}
          data-testid="mobile-view-toggle"
        >
          {mobileView === 'cards'
            ? <><LayoutGrid className="h-4 w-4" /> Grid view</>
            : <><List className="h-4 w-4" /> Card view</>}
        </Button>
      </div>

      <Card className="overflow-hidden border-2 border-foreground/10">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg font-medium mb-1">{filterStatus === 'pending' ? 'No devices marked pending to sell' : 'No phones yet'}</p>
            <p className="text-sm mb-4">Click &quot;Add Row&quot; to start your inventory.</p>
            <Button onClick={addRow}><Plus className="h-4 w-4 mr-2" /> Add Row</Button>
          </div>
        ) : (
          <>
          {/* Mobile card view — desktop table below is hidden < md */}
          <div className={`md:hidden ${mobileView === 'grid' ? 'hidden' : ''} divide-y divide-border/50`} data-testid="inventory-mobile-list">
            {filtered.map((item) => {
              const row = computeRow(item);
              const sold = row.status === 'sold';
              const pending = !!item.pending_sale;
              const seq = sequenceById.get(item.id);
              const paid = Number(row.purchase_price || 0);
              const repair = Number(row.repair_costs || 0);
              const target = row.target_sale_price != null ? Number(row.target_sale_price) : null;
              const actual = row.actual_price != null ? Number(row.actual_price) : null;
              const profit = row.actual_profit != null ? Number(row.actual_profit)
                : row.target_profit != null ? Number(row.target_profit) : null;
              const money = (v) => v == null ? '—' : `$${Math.round(v).toLocaleString()}`;
              return (
                <div
                  key={item.id}
                  className={`p-3 space-y-2 ${pending ? 'bg-sheet-soft/30 border-l-2 border-l-sheet' : sold ? 'bg-money-soft/20' : ''} ${selectedIds.has(item.id) ? 'bg-destructive/5' : ''}`}
                  data-testid={`inventory-mobile-row-${item.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                      className="mt-1 shrink-0"
                      data-testid={`mobile-row-checkbox-${item.id}`}
                    />
                    <span className="text-[10px] font-semibold text-muted-foreground tabular-nums shrink-0 mt-1 min-w-[26px] text-right">
                      #{seq ?? '—'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-foreground break-words leading-tight">
                          {[item.brand, item.model].filter(Boolean).join(' ') || <span className="text-muted-foreground italic">Unnamed</span>}
                        </p>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold shrink-0 ${
                            sold
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                              : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                          }`}
                        >
                          {sold ? 'Sold' : 'In stock'}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 break-all">
                        {item.date_purchased || '—'}
                        {item.imei && (
                          <> · <span className="font-mono tabular-nums">{item.imei}</span></>
                        )}
                      </p>
                      {(item.condition_grade || item.battery_health) && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {item.condition_grade && <span>{item.condition_grade}</span>}
                          {item.battery_health && (
                            <>{item.condition_grade && ' · '}Battery {item.battery_health}%</>
                          )}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Storage</span>
                        <Select value={item.storage || 'ANY'} onValueChange={(v) => updateField(item, 'storage', v)}>
                          <SelectTrigger className="h-6 w-28 text-[11px]" data-testid={`mobile-storage-${item.id}`}>
                            <SelectValue placeholder="ANY" />
                          </SelectTrigger>
                          <SelectContent>
                            {(((sheetModels.find(m => m.model === item.model)?.storages) || []).length
                              ? sheetModels.find(m => m.model === item.model).storages
                              : ['ANY']).map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {showIssue && item.issue && (
                        <p className="text-[11px] text-destructive/80 mt-0.5 break-words">
                          Issue: {item.issue}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2 text-center border-t border-border/50 pt-2">
                    <div>
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Paid</p>
                      <p className="text-xs font-semibold tabular-nums">{money(paid + repair)}</p>
                      {repair > 0 && <p className="text-[9px] text-muted-foreground">+${repair} rep.</p>}
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Target</p>
                      <p className="text-xs font-semibold tabular-nums">{money(target)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Actual</p>
                      <p className="text-xs font-semibold tabular-nums">{money(actual)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Profit</p>
                      <p className={`text-xs font-bold tabular-nums ${profit != null && profit < 0 ? 'text-destructive' : 'text-money'}`}>
                        {money(profit)}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 text-xs ${pending ? 'text-sheet' : 'text-muted-foreground hover:text-sheet'}`}
                      onClick={() => setPending([item.id], !pending)}
                      data-testid={`mobile-toggle-pending-${item.id}`}
                    >
                      <Flag className={`h-3.5 w-3.5 mr-1 ${pending ? 'fill-current' : ''}`} /> {pending ? 'Pending' : 'Mark pending'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(item)}
                      data-testid={`mobile-delete-row-${item.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
            <div className="p-3 border-t border-border/50">
              <Button onClick={addRow} className="w-full" data-testid="mobile-add-row">
                <Plus className="h-4 w-4 mr-2" /> Add Phone
              </Button>
            </div>
            <p className="px-3 pb-3 text-[10px] text-muted-foreground text-center">
              Tip: for fast inline editing, rotate your phone sideways or use a desktop.
            </p>
          </div>

          <div className={`${mobileView === 'grid' ? 'block' : 'hidden'} md:block overflow-x-auto`}>
            <table className="w-full text-xs border-collapse table-fixed select-none" data-testid="inventory-grid">
              <colgroup>
                <col className="w-[34px]" />
                <col className="w-[36px]" />
                <col className="w-[80px]" />
                <col />
                <col className="w-[100px]" />
                <col className="w-[52px]" />
                <col className="w-[90px]" />
                {showIssue && <col />}
                <col className="w-[70px]" />
                <col className="w-[70px]" />
                <col className="w-[78px]" />
                <col className="w-[70px]" />
                <col className="w-[82px]" />
                <col className="w-[78px]" />
                <col className="w-[64px]" />
              </colgroup>
              <thead>
                <tr className="bg-sheet text-white font-display tracking-wider text-[10px]">
                  <th className="px-1 py-2.5 text-center border-r border-white/20 sticky left-0 z-20 bg-sheet" title="Row number">#</th>
                  <th className="px-1.5 py-2.5 text-center border-r border-white/20 sticky left-[34px] z-20 bg-sheet">
                    <Checkbox
                      checked={allFilteredSelected ? true : (someFilteredSelected ? 'indeterminate' : false)}
                      onCheckedChange={(c) => toggleSelectAll(!!c, filtered)}
                      className="border-white/60 data-[state=checked]:bg-white data-[state=checked]:text-sheet"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-1.5 py-2.5 text-left border-r border-white/20">DATE</th>
                  <th className="px-1.5 py-2.5 text-left border-r border-white/20">PHONE</th>
                  <th className="px-1.5 py-2.5 text-left border-r border-white/20">IMEI</th>
                  <th className="px-1.5 py-2.5 text-center border-r border-white/20">BAT%</th>
                  <th className="px-1.5 py-2.5 text-left border-r border-white/20">GRADE</th>
                  {showIssue && <th className="px-1.5 py-2.5 text-left border-r border-white/20" data-testid="issue-col-header">ISSUE</th>}
                  <th className="px-1.5 py-2.5 text-right border-r border-white/20">PAID</th>
                  <th className="px-1.5 py-2.5 text-right border-r border-white/20">TARGET</th>
                  <th className="px-1.5 py-2.5 text-right border-r border-white/20 bg-money/90">T.PROFIT</th>
                  <th className="px-1.5 py-2.5 text-right border-r border-white/20">ACTUAL</th>
                  <th className="px-1.5 py-2.5 text-left border-r border-white/20">SOLD</th>
                  <th className="px-1.5 py-2.5 text-right border-r border-white/20 bg-money/90">A.PROFIT</th>
                  <th className="px-1.5 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, rowIndex) => {
                  const pending = !!item.pending_sale;
                  const inFlagPreview = flagPreview && rowIndex >= flagPreview.lo && rowIndex <= flagPreview.hi;
                  const rowStorages = (sheetModels.find(m => m.model === item.model)?.storages) || [];
                  const storageOpts = rowStorages.length ? rowStorages : ['ANY'];
                  return (
                  <tr
                    key={item.id}
                    onMouseEnter={() => onFlagEnter(rowIndex)}
                    className={`border-b border-foreground/5 ${pending ? 'bg-sheet-soft/40' : item.status === 'sold' ? 'bg-money-soft/30' : ''} ${selectedIds.has(item.id) ? 'bg-destructive/5' : ''} ${inFlagPreview ? 'ring-1 ring-inset ring-sheet/60' : ''}`}
                    data-testid={`inventory-row-${item.id}`}
                  >
                    <td
                      className="px-1 py-1 text-center text-[10px] font-semibold text-muted-foreground tabular-nums border-r border-foreground/5 sticky left-0 z-10 bg-card"
                      data-testid={`row-index-${item.id}`}
                    >
                      {sequenceById.get(item.id) ?? (rowIndex + 1)}
                    </td>
                    <td className="px-1.5 py-1 text-center border-r border-foreground/5 sticky left-[34px] z-10 bg-card">
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={() => toggleSelect(item.id)}
                        data-testid={`row-checkbox-${item.id}`}
                      />
                    </td>
                    <EditableCell
                      type="date" value={dateValue(item.date_purchased)}
                      onSave={v => updateField(item, 'date_purchased', v)}
                      testId={`cell-date-${item.id}`}
                    />
                    <EditableCell
                      type="model" value={item.model}
                      options={sheetModels}
                      onSave={v => updateField(item, 'model', v)}
                      storageValue={item.storage}
                      storageOptions={storageOpts}
                      onStorageSave={v => updateField(item, 'storage', v)}
                      testId={`cell-model-${item.id}`}
                      placeholder="iPhone 15 Pro"
                      className="font-medium max-w-0"
                    />
                    <EditableCell
                      type="text" value={item.imei}
                      onSave={v => updateField(item, 'imei', v)}
                      testId={`cell-imei-${item.id}`}
                      placeholder="—"
                      tone={item.status === 'sold' ? 'sold' : undefined}
                      format="imei"
                      sold={item.status === 'sold'}
                    />
                    <EditableCell
                      type="optcustom" value={item.battery_health}
                      options={BATTERY_OPTIONS}
                      onSave={v => updateField(item, 'battery_health', v)}
                      testId={`cell-battery-${item.id}`}
                      placeholder="Battery"
                    />
                    <EditableCell
                      type="optcustom" value={item.condition_grade}
                      options={GRADES.map(g => ({ value: g, label: g }))}
                      onSave={v => updateField(item, 'condition_grade', v)}
                      testId={`cell-grade-${item.id}`}
                      format="grade"
                    />
                    {showIssue && (
                      <EditableCell
                        type="optcustom" value={item.issue}
                        options={ISSUE_OPTIONS}
                        onSave={v => updateField(item, 'issue', v)}
                        testId={`cell-issue-${item.id}`}
                        placeholder="None"
                        className="truncate max-w-0"
                      />
                    )}
                    <EditableCell
                      type="number" value={item.purchase_price}
                      onSave={v => updateField(item, 'purchase_price', parseFloat(v) || 0)}
                      testId={`cell-price-paid-${item.id}`}
                      align="right"
                      {...numCellProps(rowIndex, item, 'purchase_price', 1)}
                    />
                    <EditableCell
                      type="number" value={item.target_sale_price}
                      onSave={v => updateField(item, 'target_sale_price', v === '' ? null : parseFloat(v) || 0)}
                      testId={`cell-target-${item.id}`}
                      align="right"
                      {...numCellProps(rowIndex, item, 'target_sale_price', 2)}
                    />
                    <EditableCell
                      type="computed" value={item.target_profit}
                      testId={`cell-target-profit-${item.id}`}
                      align="right"
                      className="bg-money-soft/30"
                      {...numCellProps(rowIndex, item, 'target_profit', 3)}
                    />
                    <EditableCell
                      type="number" value={item.actual_price}
                      onSave={v => updateField(item, 'actual_price', v === '' ? null : parseFloat(v) || 0)}
                      testId={`cell-actual-${item.id}`}
                      align="right"
                      {...numCellProps(rowIndex, item, 'actual_price', 4)}
                    />
                    <EditableCell
                      type="date" value={dateValue(item.sold_date)}
                      onSave={v => updateField(item, 'sold_date', v)}
                      testId={`cell-sold-date-${item.id}`}
                    />
                    <EditableCell
                      type="computed" value={item.actual_profit}
                      testId={`cell-actual-profit-${item.id}`}
                      align="right"
                      className="bg-money-soft/30"
                      {...numCellProps(rowIndex, item, 'actual_profit', 5)}
                    />
                    <td className="px-1 py-0.5">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          type="button"
                          onMouseDown={(e) => onFlagDown(e, rowIndex, item)}
                          onMouseEnter={() => onFlagEnter(rowIndex)}
                          className={`h-6 w-6 inline-flex items-center justify-center rounded transition-colors cursor-pointer ${pending ? 'text-sheet' : 'text-muted-foreground hover:text-sheet'} ${inFlagPreview ? 'bg-sheet-soft/70 ring-1 ring-sheet' : ''}`}
                          data-testid={`toggle-pending-${item.id}`}
                          title={pending ? 'Remove from pending · drag up/down to unmark a range' : 'Mark pending to sell · drag down to mark a range'}
                        >
                          <Flag className={`h-3.5 w-3.5 ${pending ? 'fill-current' : ''}`} />
                        </button>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(item)}
                          data-testid={`delete-row-${item.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {/* Add Row footer */}
                <tr>
                  <td colSpan={showIssue ? 15 : 14} className="p-0">
                    <button
                      onClick={addRow}
                      className="w-full text-left px-4 py-2.5 text-xs text-muted-foreground hover:bg-sheet-soft/50 transition-colors border-t-2 border-dashed border-foreground/15 flex items-center gap-2"
                      data-testid="add-row-inline"
                    >
                      <Plus className="h-3.5 w-3.5" /> Click to add a new row
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      {/* Bulk delete confirm */}
      <Dialog open={bulkConfirmOpen} onOpenChange={(o) => !bulkDeleting && setBulkConfirmOpen(o)}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              DELETE {selectedIds.size} PHONE{selectedIds.size === 1 ? '' : 'S'}?
            </DialogTitle>
            <DialogDescription>
              This will permanently remove the selected phones from inventory. Any auto-synced sales records will also be removed.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} disabled={bulkDeleting} data-testid="bulk-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-confirm-delete-btn"
            >
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Phone via Form Dialog */}
      <Dialog open={addFormOpen} onOpenChange={(o) => { setAddFormOpen(o); if (!o) { setFormData(emptyForm); setGradeCustom(false); setBatteryCustom(false); setIssueCustom(false); setAutoInfo(null); setModelSearch(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wider">ADD PHONE</DialogTitle>
            <DialogDescription>Add a new phone to your inventory. Mark it sold by filling Actual Sale Price + Sold Date.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2 col-span-2">
              <Label>Phone Model *</Label>
              <div className="flex gap-2">
                <Popover open={modelPickOpen} onOpenChange={setModelPickOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button" variant="outline" role="combobox"
                      className="flex-1 justify-between font-normal min-w-0"
                      data-testid="form-model"
                    >
                      <span className={`truncate ${formData.model ? '' : 'text-muted-foreground'}`}>
                        {formData.model || 'Search your price sheet…'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Type a model…"
                        value={modelSearch}
                        onValueChange={setModelSearch}
                        data-testid="form-model-search"
                      />
                      <CommandList>
                        <CommandEmpty>
                          {modelSearch ? (
                            <button
                              type="button"
                              className="text-xs text-primary hover:underline px-2 py-1"
                              onClick={() => { setFormData(f => ({ ...f, model: modelSearch, storage: 'ANY' })); setModelPickOpen(false); }}
                              data-testid="form-model-use-custom"
                            >
                              Use “{modelSearch}” as a custom name
                            </button>
                          ) : <span className="text-xs text-muted-foreground px-2 py-1">No models on your price sheet yet.</span>}
                        </CommandEmpty>
                        <CommandGroup>
                          {sheetModels.map(m => (
                            <CommandItem
                              key={m.model}
                              value={m.model}
                              onSelect={() => {
                                const first = (m.storages && m.storages[0]) || 'ANY';
                                setFormData(f => ({ ...f, model: m.model, storage: first }));
                                setModelPickOpen(false);
                              }}
                              data-testid={`form-model-opt-${m.model}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${formData.model === m.model ? 'opacity-100' : 'opacity-0'}`} />
                              {m.model}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Select value={formData.storage || 'ANY'} onValueChange={v => setFormData({ ...formData, storage: v })}>
                  <SelectTrigger className="w-32 shrink-0" data-testid="form-storage"><SelectValue placeholder="Storage" /></SelectTrigger>
                  <SelectContent>
                    {(sheetModels.find(m => m.model === formData.model)?.storages || ['ANY']).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[11px] text-muted-foreground">Pick a model from your price sheet to auto-fill the target — or type your own custom name.</p>
            </div>
            <div className="space-y-2">
              <Label>Date Purchased</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="form-date-purchased"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date_purchased
                      ? fmtDate(parseDate(formData.date_purchased, 'yyyy-MM-dd', new Date()), 'PPP')
                      : <span className="text-muted-foreground">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date_purchased ? parseDate(formData.date_purchased, 'yyyy-MM-dd', new Date()) : undefined}
                    onSelect={(d) => d && setFormData({ ...formData, date_purchased: fmtDate(d, 'yyyy-MM-dd') })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Condition / Grade</Label>
              {gradeCustom ? (
                <div className="flex gap-2">
                  <Input value={formData.condition_grade} onChange={e => setFormData({ ...formData, condition_grade: e.target.value })} placeholder="Custom grade" data-testid="form-grade-custom" autoFocus />
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setGradeCustom(false); setFormData({ ...formData, condition_grade: 'Grade B' }); }}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <Select
                  value={GRADES.includes(formData.condition_grade) ? formData.condition_grade : CUSTOM}
                  onValueChange={v => { if (v === CUSTOM) { setGradeCustom(true); setFormData({ ...formData, condition_grade: '' }); } else setFormData({ ...formData, condition_grade: v }); }}
                >
                  <SelectTrigger data-testid="form-grade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    <SelectItem value={CUSTOM}>Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>IMEI</Label>
              <Input
                value={formData.imei}
                onChange={e => setFormData({ ...formData, imei: e.target.value })}
                placeholder="35XXXXXX..."
                data-testid="form-imei"
              />
            </div>
            <div className="space-y-2">
              <Label>Battery</Label>
              {batteryCustom ? (
                <div className="flex gap-2">
                  <Input value={formData.battery_health} onChange={e => setFormData({ ...formData, battery_health: e.target.value })} placeholder="e.g. 88%" data-testid="form-battery-custom" autoFocus />
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setBatteryCustom(false); setFormData({ ...formData, battery_health: 'normal' }); }}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <Select
                  value={BATTERY_OPTIONS.some(o => o.value === formData.battery_health) ? formData.battery_health : CUSTOM}
                  onValueChange={v => { if (v === CUSTOM) { setBatteryCustom(true); setFormData({ ...formData, battery_health: '' }); } else setFormData({ ...formData, battery_health: v }); }}
                >
                  <SelectTrigger data-testid="form-battery"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BATTERY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    <SelectItem value={CUSTOM}>Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Price Paid ($) *</Label>
              <Input
                type="number" step="0.01" value={formData.purchase_price}
                onChange={e => setFormData({ ...formData, purchase_price: e.target.value })}
                placeholder="400"
                data-testid="form-price-paid"
              />
            </div>
            <div className="space-y-2">
              <Label>Repair Costs ($)</Label>
              <Input
                type="number" step="0.01" value={formData.repair_costs}
                onChange={e => setFormData({ ...formData, repair_costs: e.target.value })}
                placeholder="0"
                data-testid="form-repair-costs"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Target Sale Price ($)
                {autoLoading
                  ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  : autoInfo?.found ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-money"><Sparkles className="h-3 w-3" /> AUTO</span> : null}
              </Label>
              <Input
                type="number" step="0.01" value={formData.target_sale_price}
                onChange={e => setFormData({ ...formData, target_sale_price: e.target.value })}
                placeholder="550"
                data-testid="form-target-price"
              />
              {formData.target_sale_price !== '' && <p className="text-sm font-semibold text-money" data-testid="form-projected-profit">Projected profit: ${(Number(formData.target_sale_price) - Number(formData.purchase_price || 0) - Number(formData.repair_costs || 0)).toLocaleString()}</p>}
              {autoInfo && (
                <p className="text-[11px] text-muted-foreground" data-testid="form-target-hint">
                  {autoInfo.found ? (
                    <>
                      Auto from sheet ({autoInfo.carrier}):{' '}
                      {autoInfo.breakdown?.map((b, i) => (
                        <span key={i}>
                          {i > 0 ? ' · ' : ''}{b.label}: {b.amount < 0 ? '−$' + Math.abs(b.amount).toLocaleString() : '$' + Number(b.amount).toLocaleString()}
                        </span>
                      ))}
                      {autoInfo.has_ask ? ' · set the ASK issue amount yourself' : ''}. Edit if needed.
                    </>
                  ) : autoInfo.note}
                </p>
              )}
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Issue</Label>
              <p className="text-[11px] text-muted-foreground" data-testid="issue-deduction-help">Choose a carrier price or type a deduction, e.g. Carrier locked -$40. Explicit amounts replace preset issue deductions.</p>
              {issueCustom ? (
                <div className="flex gap-2">
                  <Input value={formData.issue} onChange={e => setFormData({ ...formData, issue: e.target.value })} placeholder="Describe the issue" data-testid="form-issue-custom" autoFocus />
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setIssueCustom(false); setFormData({ ...formData, issue: 'None' }); }}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <Select
                  value={ISSUE_OPTIONS.some(o => o.value === formData.issue) ? formData.issue : CUSTOM}
                  onValueChange={v => { if (v === CUSTOM) { setIssueCustom(true); setFormData({ ...formData, issue: '' }); } else setFormData({ ...formData, issue: v }); }}
                >
                  <SelectTrigger data-testid="form-issue"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ISSUE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    <SelectItem value={CUSTOM}>Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Actual Sale Price ($)</Label>
              <Input
                type="number" step="0.01" value={formData.actual_price}
                onChange={e => setFormData({ ...formData, actual_price: e.target.value })}
                placeholder="Leave blank if not sold"
                data-testid="form-actual-price"
              />
            </div>
            {formData.actual_price && (
              <div className="grid grid-cols-2 gap-3 rounded-md border border-foreground/10 p-3 bg-muted/30" data-testid="form-payment-block">
                <div className="space-y-1.5">
                  <Label className="text-xs">Purchased with</Label>
                  <Select value={formData.purchase_source} onValueChange={v => setFormData({ ...formData, purchase_source: v })}>
                    <SelectTrigger data-testid="form-purchase-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Payout to</Label>
                  <Select value={formData.payment_destination} onValueChange={v => setFormData({ ...formData, payment_destination: v })}>
                    <SelectTrigger data-testid="form-payment-destination"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank (KNO3 default)</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment status</Label>
                  <Select value={formData.payment_status} onValueChange={v => setFormData({ ...formData, payment_status: v })}>
                    <SelectTrigger data-testid="form-payment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending (no money yet)</SelectItem>
                      <SelectItem value="paid">Paid (add to account)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Buyer / reference</Label>
                  <Input value={formData.buyer_name} onChange={e => setFormData({ ...formData, buyer_name: e.target.value })} placeholder="e.g. KNO3" data-testid="form-buyer-name" />
                </div>
                <p className="col-span-2 text-[11px] text-muted-foreground">Money is added to the chosen account only when status is <strong>Paid</strong> — once. Pending sales show under Unpaid Sales.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Sold Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="form-sold-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.sold_date
                      ? fmtDate(parseDate(formData.sold_date, 'yyyy-MM-dd', new Date()), 'PPP')
                      : <span className="text-muted-foreground">Leave blank if not sold</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.sold_date ? parseDate(formData.sold_date, 'yyyy-MM-dd', new Date()) : undefined}
                    onSelect={(d) => d && setFormData({ ...formData, sold_date: fmtDate(d, 'yyyy-MM-dd') })}
                    initialFocus
                  />
                  <div className="flex justify-end p-2 border-t bg-muted/30">
                    <Button variant="ghost" size="sm" onClick={() => setFormData({ ...formData, sold_date: '' })}>Clear</Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddFormOpen(false)}>Cancel</Button>
            <Button onClick={submitForm} data-testid="form-submit">Add Phone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(o) => { setImportDialogOpen(o); if (!o) setImportReplaceAll(false); }}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-wider">IMPORT INVENTORY FROM CSV</DialogTitle>
            <DialogDescription>Upload a CSV exported from Google Sheets to add or replace your inventory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV with columns: <strong>Date, Phone, IMEI, Battery Health, Condition/Grade, Issue, Price Paid, Repair Costs, Target Sale Price, Actual Price, Sold Date</strong>.
            </p>
            <p className="text-xs text-muted-foreground">Exported Google Sheets CSVs work. Target/Actual Profit auto-calculate.</p>

            {/* Replace-all toggle */}
            <label
              className={`flex items-start gap-3 rounded-md border px-3 py-3 cursor-pointer transition-colors ${importReplaceAll ? 'border-destructive bg-destructive/5' : 'border-border hover:bg-muted/40'}`}
              data-testid="replace-all-label"
            >
              <Checkbox
                checked={importReplaceAll}
                onCheckedChange={(c) => setImportReplaceAll(!!c)}
                className="mt-0.5"
                data-testid="replace-all-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-2">
                  Replace all existing phones
                  {importReplaceAll && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {importReplaceAll
                    ? <span className="text-destructive">Your current {items.length} phone{items.length === 1 ? '' : 's'} will be permanently deleted before the new ones are imported.</span>
                    : <span>New rows will be added on top of what you already have.</span>
                  }
                </div>
              </div>
            </label>

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <label className="cursor-pointer">
                <span className="text-sm font-medium text-primary hover:underline">
                  {importing ? 'Importing...' : 'Choose CSV file'}
                </span>
                <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} data-testid="csv-file-input" />
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Spreadsheet-style selection summary (Sum / Avg / Count / Min / Max) */}
      {selectionAgg && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-full border border-foreground/15 bg-foreground text-background shadow-xl pl-1 pr-1.5 py-1"
          data-testid="selection-sum-bar"
        >
          <button
            type="button"
            onClick={() => setAggMode(m => {
              const order = ['sum', 'avg', 'count', 'min', 'max'];
              return order[(order.indexOf(m) + 1) % order.length];
            })}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 hover:bg-background/15 transition-colors"
            data-testid="selection-agg-pill"
            title="Click to switch Sum / Average / Count / Min / Max"
          >
            <Sigma className="h-3.5 w-3.5 opacity-80" />
            <span className="text-[11px] uppercase tracking-widest opacity-70">{aggMode}</span>
            <span className="font-display text-base tabular-nums" data-testid="selection-agg-value">
              {aggMode === 'count'
                ? selectionAgg.cells
                : `$${selectionAgg[aggMode].toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
            </span>
          </button>
          <span className="text-[10px] uppercase tracking-wider opacity-60 px-1" data-testid="selection-cell-count">
            {selectionAgg.cells} cell{selectionAgg.cells === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => setSelectedCells(new Set())}
            className="rounded-full p-1.5 hover:bg-background/15 transition-colors"
            data-testid="selection-clear-btn"
            title="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
